import React, { useState, useEffect } from 'react';
import { Search, MapPin, Navigation, Clock, User, RefreshCw } from 'lucide-react';
import axios from 'axios';

interface LocationPoint {
    id: number;
    user_id: number;
    user_username: string;
    user_avatar?: string;
    latitude: number;
    longitude: number;
    accuracy?: number;
    speed?: number;
    heading?: number;
    timestamp: string;
}

interface HistoryStats {
    total_points: number;
    users_with_history: number;
    today_points: number;
}

const LocationHistoryManagement: React.FC = () => {
    const [history, setHistory] = useState<LocationPoint[]>([]);
    const [stats, setStats] = useState<HistoryStats | null>(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [userId, setUserId] = useState<string>('');

    useEffect(() => {
        fetchData();
    }, [userId]);

    const fetchData = async () => {
        setRefreshing(true);
        try {
            const url = userId ? `https://api.lifeshare.app/api/v1/admin/location-history?user_id=${userId}` : 'https://api.lifeshare.app/api/v1/admin/location-history';
            const [historyRes, statsRes] = await Promise.all([
                axios.get(url),
                axios.get('https://api.lifeshare.app/api/v1/admin/location-history/stats')
            ]);
            setHistory(historyRes.data.history || []);
            setStats(statsRes.data);
        } catch (error) {
            console.error('Fetch error:', error);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    const formatSpeed = (speed?: number) => speed ? `${(speed * 3.6).toFixed(1)} km/s` : '-';
    const formatHeading = (heading?: number) => {
        if (heading === undefined) return '-';
        const directions = ['K', 'KD', 'D', 'GD', 'G', 'GB', 'B', 'KB'];
        return directions[Math.round(heading / 45) % 8];
    };

    const filteredHistory = history.filter(h => (h.user_username || '').toLowerCase().includes(searchText.toLowerCase()));

    if (loading) {
        return (
            <div className="flex items-center justify-center py-20">
                <div className="w-5 h-5 border-2 border-purple-300 border-t-purple-600 rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="max-w-5xl mx-auto space-y-6">

                {/* Header */}
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl md:text-3xl font-semibold text-gray-900">Konum Geçmişi</h1>
                        <p className="text-sm text-gray-500 mt-1">Kullanıcı konum geçmişlerini görüntüle</p>
                    </div>
                    <button onClick={fetchData} disabled={refreshing} className="p-2.5 bg-purple-600 text-white rounded-xl hover:bg-purple-700 transition-colors">
                        <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
                    </button>
                </div>

                {/* Stats */}
                {stats && (
                    <div className="grid grid-cols-3 gap-3">
                        <div className="bg-gradient-to-br from-purple-50 to-purple-100 border border-purple-200 rounded-2xl p-4">
                            <div className="flex items-center gap-2 mb-1 opacity-70"><MapPin className="w-4 h-4" /></div>
                            <p className="text-2xl font-bold text-purple-700">{(stats.total_points || 0).toLocaleString()}</p>
                            <p className="text-xs text-purple-600">Toplam Nokta</p>
                        </div>
                        <div className="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 rounded-2xl p-4">
                            <div className="flex items-center gap-2 mb-1 opacity-70"><User className="w-4 h-4" /></div>
                            <p className="text-2xl font-bold text-blue-700">{stats.users_with_history}</p>
                            <p className="text-xs text-blue-600">Kullanıcı</p>
                        </div>
                        <div className="bg-gradient-to-br from-green-50 to-green-100 border border-green-200 rounded-2xl p-4">
                            <div className="flex items-center gap-2 mb-1 opacity-70"><Clock className="w-4 h-4" /></div>
                            <p className="text-2xl font-bold text-green-700">{stats.today_points}</p>
                            <p className="text-xs text-green-600">Bugün</p>
                        </div>
                    </div>
                )}

                {/* Search */}
                <div className="bg-white rounded-2xl p-4">
                    <div className="flex flex-col sm:flex-row gap-3">
                        <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input type="text" placeholder="Kullanıcı ara..." value={searchText} onChange={(e) => setSearchText(e.target.value)} className="w-full pl-10 pr-4 py-3 bg-gray-50 rounded-xl text-sm placeholder-gray-400 outline-none focus:ring-2 focus:ring-purple-500" />
                        </div>
                        <input type="number" placeholder="User ID" value={userId} onChange={(e) => setUserId(e.target.value)} className="px-4 py-3 bg-gray-50 rounded-xl text-sm outline-none focus:ring-2 focus:ring-purple-500 w-32" />
                    </div>
                </div>

                {/* List */}
                {filteredHistory.length === 0 ? (
                    <div className="bg-white rounded-2xl p-12 text-center">
                        <MapPin className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                        <p className="text-gray-500">Konum geçmişi bulunamadı</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {filteredHistory.map((loc) => (
                            <div key={loc.id} className="bg-white rounded-2xl p-4 hover:ring-2 hover:ring-purple-100 transition-all">
                                <div className="flex items-start gap-3">
                                    {loc.user_avatar ? (
                                        <img src={`https://api.lifeshare.app${loc.user_avatar}`} alt="" className="w-10 h-10 rounded-xl object-cover flex-shrink-0" />
                                    ) : (
                                        <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center text-purple-600 flex-shrink-0">
                                            <MapPin className="w-5 h-5" />
                                        </div>
                                    )}
                                    <div className="min-w-0 flex-1">
                                        <p className="font-semibold text-gray-900 text-sm truncate">@{loc.user_username}</p>
                                        <p className="text-xs text-gray-500">{new Date(loc.timestamp).toLocaleString('tr-TR')}</p>
                                    </div>
                                </div>

                                <div className="mt-3 p-3 bg-gray-50 rounded-xl text-xs space-y-1">
                                    <div className="flex justify-between">
                                        <span className="text-gray-500">Koordinat:</span>
                                        <span className="font-mono">{loc.latitude.toFixed(4)}, {loc.longitude.toFixed(4)}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-500">Hız:</span>
                                        <span className="font-medium">{formatSpeed(loc.speed)}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-500">Yön:</span>
                                        <span className="flex items-center gap-1"><Navigation className="w-3 h-3" />{formatHeading(loc.heading)}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-500">Doğruluk:</span>
                                        <span>{loc.accuracy ? `${loc.accuracy.toFixed(0)}m` : '-'}</span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default LocationHistoryManagement;
